import { createContext, useState, useEffect, useMemo, useCallback } from 'react';

import type { AppContextType, Theme } from './types';
import type { Organization } from './types/organization';

const AppContext = createContext<AppContextType>({
  theme: 'light',
  cookiesAccepted: false,
  selectedOrganization: null,
});

const applyScaleMode = (theme: string) => {
  document.documentElement.dataset.mode = theme;
};

const getStorage = (): Storage | null => {
  const storage = globalThis.localStorage as Partial<Storage> | undefined;
  if (!storage) {
    return null;
  }

  if (typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function') {
    return null;
  }

  return storage as Storage;
};

const getInitialTheme = (): Theme => {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const systemTheme: Theme = mq.matches ? 'dark' : 'light';
  const localStorageTheme = getStorage()?.getItem('theme') as Theme | null;
  return localStorageTheme ?? systemTheme;
};

const getInitialCookieConsent = () => getStorage()?.getItem('cookiesAccepted') === 'true';

const AppContextProvider = ({ children }: { children: React.ReactElement }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [cookiesAccepted, setCookiesAccepted] = useState(getInitialCookieConsent);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  useEffect(() => {
    applyScaleMode(theme);

    const handleSystemChange = (e: { matches: unknown }) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setTheme(newSystemTheme);
    };
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', handleSystemChange);

    return () => {
      mq.removeEventListener('change', handleSystemChange);
    };
  }, [theme]);

  useEffect(() => {
    applyScaleMode(theme);
    getStorage()?.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    if (cookiesAccepted) {
      getStorage()?.setItem('cookiesAccepted', 'true');
    }
  }, [cookiesAccepted]);

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
  }, []);

  const contextValue: AppContextType = useMemo(
    () => ({
      theme,
      toggleTheme,
      cookiesAccepted,
      setCookiesAccepted,
      selectedOrganization,
      setSelectedOrganization,
    }),
    [theme, cookiesAccepted, selectedOrganization, toggleTheme]
  );

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export { AppContext, AppContextProvider };
